import React, { memo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import type { TimelineBlock, TimelineEvent } from '../../../types/backend';
import BlockShell from './BlockShell';

interface Props {
  block: TimelineBlock;
}

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default' | 'info'> = {
  resolved: 'success',
  ok: 'success',
  healthy: 'success',
  completed: 'success',
  done: 'success',
  open: 'info',
  detected: 'info',
  acknowledged: 'info',
  investigating: 'warning',
  degraded: 'warning',
  warning: 'warning',
  failed: 'error',
  error: 'error',
  critical: 'error',
};

const TimelineRow: React.FC<{ event: TimelineEvent; isLast: boolean }> = ({ event, isLast }) => {
  const statusColor = event.status ? (STATUS_COLORS[event.status.toLowerCase()] ?? 'default') : undefined;
  const dotColor = statusColor && statusColor !== 'default' ? `${statusColor}.main` : 'primary.main';

  return (
    <Box component="li" sx={{ display: 'flex', gap: 1.5, listStyle: 'none' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          pt: 0.25,
        }}
      >
        <Box
          sx={{
            width: 11,
            height: 11,
            borderRadius: '50%',
            bgcolor: dotColor,
            border: '2px solid',
            borderColor: 'background.paper',
            boxShadow: (theme) => `0 0 0 1px ${theme.palette.divider}`,
          }}
        />
        {!isLast && <Box sx={{ flex: 1, width: '2px', bgcolor: 'divider', my: 0.25 }} />}
      </Box>
      <Box sx={{ pb: isLast ? 0 : 2, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {event.time}
          </Typography>
          {event.status && (
            <Chip
              label={event.status}
              color={statusColor}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 500 }}
            />
          )}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mt: 0.25 }}>
          {event.label}
        </Typography>
        {event.detail && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
            {event.detail}
          </Typography>
        )}
        {event.branches && event.branches.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
            {event.branches.map((branch, i) => (
              <Box
                key={i}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 999,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.3 }}
                >
                  {branch.label}
                </Typography>
                {branch.detail && (
                  <Typography variant="caption" color="text.secondary">
                    {branch.detail}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const TimelineBlockView: React.FC<Props> = memo(({ block }) => (
  <BlockShell bodyPadding={2}>
    <Box
      component="ol"
      aria-label="timeline"
      sx={{ display: 'flex', flexDirection: 'column', m: 0, p: 0 }}
    >
      {block.events.map((event, i) => (
        <TimelineRow key={i} event={event} isLast={i === block.events.length - 1} />
      ))}
    </Box>
  </BlockShell>
));

TimelineBlockView.displayName = 'TimelineBlockView';
export default TimelineBlockView;
